import { useQuery } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";

export interface UserJourneyItem {
  id: string;
  name: string;
  description?: string;
  scenarioId?: string;
  projectUuid?: string;
  createdAt?: string;
  updatedAt?: string;
  citationRefs?: string[];
}

export interface FlowItem {
  id: string;
  name: string;
  description?: string;
  userJourneyIds: string[];
  createdAt?: string;
  updatedAt?: string;
  citationRefs?: string[];
}

export interface PageItem {
  id: string;
  name: string;
  description?: string;
  flowIds: string[];
  createdAt?: string;
  updatedAt?: string;
  citationRefs?: string[];
}

export interface ComponentItem {
  id: string;
  name: string;
  description?: string;
  type?: string;
  pageIds: string[];
  createdAt?: string;
  updatedAt?: string;
  citationRefs?: string[];
}

export type DesignOntologyItem =
  | UserJourneyItem
  | FlowItem
  | PageItem
  | ComponentItem;

interface DesignOntologyGraphParams {
  projectUuid: string;
  label: "UserJourney" | "Flow" | "Page" | "Component";
  filters?: Record<string, any>;
  limit?: number;
  page?: number;
}

export interface DesignOntologyGraphResponse {
  data: DesignOntologyItem[];
  total: number;
  page: number;
}

const getDesignOntologyGraphData = async ({
  projectUuid,
  label,
  filters,
  pageParam = 1,
  limit = 10,
  headers,
}: DesignOntologyGraphParams & {
  pageParam?: number;
  headers: Record<string, string>;
}): Promise<DesignOntologyGraphResponse> => {
  let url = `${config.isometricApiUrl}/design-ontology-graph/${projectUuid}/${label}?page=${pageParam}&limit=${limit}`;

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        Object.entries(value).forEach(([operator, operand]) => {
          url += `&filters[${key}][${operator}]=${operand}`;
        });
      } else {
        url += `&filters[${key}][$eq]=${value}`;
      }
    });
  }

  const response = await apiFetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${label} data: ${response.statusText}`);
  }

  const result: { data: DesignOntologyItem[]; total: number } =
    await response.json();
  return {
    data: result.data || [],
    total: result.total || 0,
    page: pageParam,
  };
};

export const useGetDesignOntologyGraphService = ({
  projectUuid,
  label,
  filters,
  limit = 10,
  page = 1,
  enabled,
}: DesignOntologyGraphParams & { enabled: boolean }) => {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: [
      "design-ontology-graph",
      projectUuid,
      label,
      page,
      limit,
      filters,
    ],
    queryFn: () =>
      getDesignOntologyGraphData({
        projectUuid,
        label,
        filters,
        pageParam: page,
        limit,
        headers: getAuthHeaders(),
      }),
    enabled: enabled && !!projectUuid,
    staleTime: 0,
  });
};
